import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, SxProps, Theme } from '@mui/material';
import { formatNumber, formatCurrency } from '../utils/helpers';

interface StatCardProps {
  title: string;
  value: number | string | undefined | null;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  formatType?: 'number' | 'currency' | 'string'; // Add format type option
  sx?: SxProps<Theme>;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, loading, error, formatType = 'number', sx }) => {
  // Format the value based on the specified format type
  const getFormattedValue = () => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return 'N/A';
    
    switch (formatType) {
      case 'currency':
        return formatCurrency(value);
      case 'number':
        return formatNumber(value);
      case 'string':
      default:
        return value.toString();
    }
  };

  const displayValue = loading
    ? <CircularProgress size={24} />
    : error
    ? <Typography color="error" variant="h6">Error</Typography>
    : <Typography variant="h4" component="div" fontWeight="bold">
        {getFormattedValue()}
      </Typography>;

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', ...sx }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          {icon && <Box sx={{ color: 'primary.main' }}>{icon}</Box>}
        </Box>
        {displayValue}
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </CardContent>
    </Card>
  );
};

export default StatCard; 